import type * as React from 'react';
import Box from '@cloudscape-design/components/box';
import Table, { type TableProps } from '@cloudscape-design/components/table';
import type { Payment } from '@etiquetachef/types';
import { formatDateTime } from '@/lib/format';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { PaymentStatusBadge } from '@/components/PaymentStatusBadge';

/**
 * Rótulos da tabela, injetados pelo caller (a tabela não chama `t()` — o
 * caller escolhe o namespace i18n).
 */
export type PaymentTableLabels = {
  paidAt: string;
  amount: string;
  method: string;
  status: string;
  provider: string;
  empty: string;
  notPaid: string;
  manual: string;
};

type Props = {
  payments: Payment[];
  locale: string;
  labels: PaymentTableLabels;
};

/**
 * Tabela de pagamentos do admin (Cloudscape `Table`).
 *
 * Origem: sem issue — commit 29c36b104, 2026-06-09 19:05 +0200 (billing do
 * admin em Cloudscape; substituiu o `PaymentTable` Tailwind do ui-shared, que já
 * não existe, mantendo a mesma interface de props/labels).
 *
 * Contrato: só apresentação — recebe `Payment[]` já lidos pelo caller. Colunas:
 * pago em (`paid_at`, ou `labels.notPaid` quando `null`), valor, método,
 * status (`PaymentStatusBadge`) e provedor (`provider`, ou `labels.manual`
 * quando `null` = baixa manual pelo admin).
 *
 * Usado por: `routes/InvoiceDetailPage.tsx` (pagamentos de uma fatura) e
 * `routes/PaymentsPage.tsx` (pagamentos do tenant), admin web.
 *
 * Dados: `Payment` (`PaymentSchema`, `packages/types/src/schemas.ts`), doc
 * `tenants/{tenantId}/payments/{paymentId}`. `amount` e `refunded_amount` em
 * CENTAVOS (unidade mínima da moeda) — `MoneyDisplay` divide por 100, o caller
 * NÃO converte. `paid_at`/`created_at` em epoch ms.
 */
export function PaymentTable({ payments, locale, labels }: Props): React.JSX.Element {
  const mono = (v: React.ReactNode): React.JSX.Element => (
    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>
  );

  const columns: TableProps.ColumnDefinition<Payment>[] = [
    {
      id: 'paidAt',
      header: labels.paidAt,
      cell: (p): React.ReactNode =>
        p.paid_at !== null ? formatDateTime(p.paid_at, locale) : labels.notPaid,
    },
    {
      id: 'amount',
      header: labels.amount,
      cell: (p): React.ReactNode => (
        <MoneyDisplay amount={p.amount} currency={p.currency} locale={locale} />
      ),
    },
    {
      id: 'method',
      header: labels.method,
      cell: (p): React.ReactNode => mono(p.payment_method_type),
    },
    {
      id: 'status',
      header: labels.status,
      cell: (p): React.ReactNode => <PaymentStatusBadge status={p.status} />,
    },
    {
      id: 'provider',
      header: labels.provider,
      cell: (p): React.ReactNode => mono(p.provider ?? labels.manual),
    },
  ];

  return (
    <Table<Payment>
      items={payments}
      trackBy="id"
      variant="container"
      columnDefinitions={columns}
      empty={
        <Box textAlign="center" color="inherit">
          {labels.empty}
        </Box>
      }
    />
  );
}
